import { useEffect, useId, useState } from 'react';
import { ArrowUpRight, Megaphone, X } from 'lucide-react';
import { Fire } from '@phosphor-icons/react/dist/icons/Fire';
import { DailyChallengeCard } from '@/components/DailyChallengeCard/DailyChallengeCard';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import streakEvidence from '../data/streak-return-snapshot.json';
import './newsletter-benefit-dialog.css';

export type NewsletterBenefit = 'habit' | 'sponsorship';

const benefits = {
  habit: {
    label: 'Build a reader habit',
    title: 'A streak worth coming back for.',
    description: 'Longer streaks go hand in hand with more return visits.',
  },
  sponsorship: {
    label: 'Earn sponsorship revenue',
    title: 'Give sponsors a spot after the quiz.',
    description:
      'Sell a sponsorship on the results screen. Atrivial tracks how many readers see it and click.',
    note: 'You find the sponsor and set the price.',
  },
};

function HabitExample() {
  const [selectedLength, setSelectedLength] = useState(7);
  const gradientId = useId();
  const points = streakEvidence.points.map((point, index) => ({
    ...point,
    x: 7 + (index * 86) / (streakEvidence.points.length - 1),
    y: 100 - point.returnRatePercent,
  }));
  const selected = points.find((point) => point.streakLength === selectedLength)!;
  const line = points.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <div className="benefit-streak-evidence">
      <div
        className="benefit-streak-comparison"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <DailyChallengeCard padding="sm" className="benefit-reader-streak">
          <div className="flex items-center gap-2">
            <Fire
              size={64}
              weight="fill"
              className="animate-fire shrink-0 text-streak-fire"
              aria-hidden="true"
            />
            <div className="flex flex-col leading-tight">
              <strong className="text-5xl font-bold tracking-tight text-glyph-primary">
                {selectedLength}
              </strong>{' '}
              <span className="text-xs font-medium text-glyph-secondary">edition streak</span>
            </div>
          </div>
        </DailyChallengeCard>
        <div className="benefit-return-rate">
          <strong>{selected.returnRatePercent}%</strong> <span>return rate</span>
        </div>
      </div>
      <figure className="benefit-return-chart" aria-label="Return rate by streak length">
        <div className="benefit-chart-heading">
          <span>Return rate</span>
          <span>Tap a streak to explore</span>
        </div>
        <div className="benefit-chart-plot">
          <div className="benefit-chart-axis" aria-hidden="true">
            <span>100%</span>
            <span>50%</span>
            <span>0%</span>
          </div>
          <svg viewBox="0 -8 100 116" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--nl-brand)" stopOpacity="0.55" />
                <stop offset="100%" stopColor="var(--nl-brand)" stopOpacity="0.03" />
              </linearGradient>
            </defs>
            {[0, 50, 100].map((y) => (
              <line key={y} x1="0" x2="100" y1={y} y2={y} className="benefit-chart-grid" />
            ))}
            <polygon points={`7,100 ${line} 93,100`} fill={`url(#${gradientId})`} />
            <polyline
              points={line}
              className="benefit-chart-line"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {points.map((point) => (
            <button
              key={point.streakLength}
              type="button"
              className="benefit-chart-point"
              style={{ left: `${point.x}%`, top: `${((point.y + 8) / 116) * 100}%` }}
              aria-label={`${point.streakLength}-edition streak: ${point.returnRatePercent}% return rate`}
              aria-pressed={selectedLength === point.streakLength}
              onClick={() => setSelectedLength(point.streakLength)}
            >
              <span />
            </button>
          ))}
        </div>
        <div className="benefit-chart-ticks" aria-hidden="true">
          {points.map((point) => (
            <span key={point.streakLength} style={{ left: `${point.x}%` }}>
              {point.streakLength}
            </span>
          ))}
        </div>
        <figcaption>Streak length · editions</figcaption>
      </figure>
      <details className="benefit-evidence-method">
        <summary tabIndex={0}>About these figures</summary>
        <p>
          Among players who completed each streak, this is the percentage who returned for the next
          edition.
        </p>
        <p>These are observed results, not proof that streaks cause a return.</p>
      </details>
    </div>
  );
}

function SponsorExample() {
  const [earningsCents, setEarningsCents] = useState(0);
  const earningsId = useId();

  useEffect(() => {
    let timer: ReturnType<typeof globalThis.setTimeout>;
    const schedulePenny = () => {
      const minDelayMs = 200;
      const maxDelayMs = 2000;
      const delayMs = minDelayMs + Math.floor(Math.random() * (maxDelayMs - minDelayMs + 1));
      timer = globalThis.setTimeout(() => {
        setEarningsCents((cents) => cents + 1);
        schedulePenny();
      }, delayMs);
    };
    schedulePenny();
    return () => globalThis.clearTimeout(timer);
  }, []);

  return (
    <div className="benefit-example">
      <div className="benefit-result benefit-result-compact">
        <span>Quiz complete</span>
        <strong>
          4<span>/5</span>
        </strong>
      </div>
      <Card color="neutral" px="none" py="none" className="mx-auto mt-4 w-full max-w-sm">
        <button
          className="group bg-bg-raised hover:bg-bg-subtle relative flex w-full cursor-pointer flex-col overflow-hidden rounded-lg text-left shadow-default ring-1 ring-outline-default/30 hover:ring-outline-default sm:flex-row"
          type="button"
          onClick={() => setEarningsCents((cents) => cents + 25)}
        >
          <span className="bg-bg-subtle flex h-20 w-full shrink-0 items-center justify-center text-glyph-secondary sm:h-auto sm:min-h-28 sm:w-28">
            <Megaphone size={32} aria-hidden="true" />
          </span>
          <span className="relative flex min-w-0 flex-1 flex-col justify-center p-4">
            <span className="mb-2 text-small font-bold tracking-wider text-glyph-secondary uppercase">
              Sponsored
            </span>
            <h3 className="font-title text-base font-bold text-glyph-primary">
              Your sponsor's brand here
            </h3>
            <span className="mt-1 text-small text-glyph-secondary">
              Share your sponsor’s story with readers.
            </span>
            <span className="mt-3 flex items-center pt-2 text-small font-bold text-accent-primary">
              Learn more <ArrowUpRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </span>
          </span>
        </button>
      </Card>
      <div className="benefit-earnings-counter" role="group" aria-labelledby={earningsId}>
        <span>
          <span id={earningsId} className="benefit-earnings-label">
            Illustrative earnings
          </span>
          <strong className="benefit-earnings-value" aria-live="off">
            {(earningsCents / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}
          </strong>
        </span>
      </div>
    </div>
  );
}

export default function NewsletterBenefitDialog({
  benefit,
  onClose,
}: {
  benefit: NewsletterBenefit;
  onClose: () => void;
}) {
  const titleId = useId();
  const content = benefits[benefit];

  return (
    <Modal
      isOpen
      onClose={onClose}
      showHeader={false}
      labelledBy={titleId}
      className="nl-marketing-page newsletter-benefit-modal"
      bodyClassName="newsletter-benefit-body"
      backdropClassName="newsletter-benefit-backdrop"
    >
      <div className="benefit-topbar">
        <span>{content.label}</span>
        <button type="button" className="benefit-close" onClick={onClose} aria-label="Close dialog">
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      <div className="benefit-content">
        <h2 id={titleId} className="h-display" tabIndex={-1} data-modal-initial-focus="true">
          {content.title}
        </h2>
        <p className="benefit-description">{content.description}</p>
        {benefit === 'habit' ? <HabitExample /> : <SponsorExample />}
        {benefit === 'sponsorship' && <p className="benefit-note">{benefits.sponsorship.note}</p>}
      </div>
    </Modal>
  );
}
